// Plugin para manejo de Flowise Chatbot
// Contiene métodos comunes compartidos entre Chatbot.vue y ChatbotManuals.vue

export default {

  /**
   * Hace scroll al final del contenedor de mensajes
   * @param {HTMLElement} messagesBox - El elemento contenedor de mensajes
   */
  scrollToBottom(messagesBox) {
    if (messagesBox) {
      messagesBox.scrollTop = messagesBox.scrollHeight
    }
  },

  /**
   * Obtiene la traducción para una clave específica
   * @param {string} language - Idioma actual ('es' o 'en')
   * @param {string} key - Clave de traducción
   * @param {Object} dictionaries - Diccionarios de traducciones
   * @returns {string} - Texto traducido
   */
  translate(language, key, dictionaries) {
    const dict = dictionaries[language] || dictionaries['es']
    return dict[key] || key
  },

  /**
   * Cambia el idioma del chatbot
   * @param {Object} params - { currentLang, newLang, messages, storageKey, dictionaries }
   * @returns {Object} - { language, messages } actualizados
   */
  setLanguage(params) {
    const { currentLang, newLang, messages, storageKey, dictionaries } = params

    if (currentLang === newLang) {
      return { language: currentLang, messages }
    }

    localStorage.setItem(storageKey, newLang)

    const confirmText = this.translate(newLang, 'langChanged', dictionaries)
    const updatedMessages = [...messages, { role: 'bot', text: confirmText }]

    return { language: newLang, messages: updatedMessages }
  },

  /**
   * Limpia el chat y reinicia el estado
   * @param {Object} params - { messages, attachedFiles, storageKey, dictionaries, language }
   * @returns {Object|null} - Nuevo estado o null si se cancela
   */
  clearChat(params) {
    const { messages, attachedFiles, storageKey, dictionaries, language } = params

    if (!messages.length) return null

    const confirmText = this.translate(language, 'confirmClear', dictionaries)
    if (!window.confirm(confirmText)) return null

    // Liberar memory de previews
    if (attachedFiles) {
      attachedFiles.forEach(file => {
        if (file.preview) URL.revokeObjectURL(file.preview)
      })
    }

    // Generar nuevo chatId
    const newChatId = `chat_${Date.now()}_${Math.random().toString(36).slice(2)}`
    localStorage.setItem(storageKey, newChatId)

    return {
      messages: [],
      attachedFiles: [],
      chatId: newChatId,
      selectedProjectId: null,
      selectIA: null,
    }
  },

  /**
   * Envía una petición de predicción al backend
   * @param {Object} payload - Datos a enviar
   * @param {string} token - Token JWT del usuario
   * @returns {Promise<Object>} - Respuesta del servidor
   */
  async sendPrediction(payload, token) {
    const headers = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    }

    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }

    const response = await fetch('/api/v2/chatbot/prediction', {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.message || `Server error ${response.status}`)
    }

    return await response.json()
  },

  /**
   * Sube archivos adjuntos al backend
   * @param {Array} files - Array de archivos File objects
   * @param {string} chatId - ID del chat actual
   * @param {string} chatflowId - ID del chatflow de Flowise
   * @param {string} token - Token JWT del usuario
   * @returns {Promise<Array>} - Array de archivos procesados
   */
  async uploadAttachments(files, chatId, chatflowId, token) {
    if (!files || files.length === 0) return []

    const formData = new FormData()
    files.forEach(fileObj => {
      formData.append('files[]', fileObj.file)
    })
    formData.append('chatId', chatId)
    formData.append('chatflowId', chatflowId)

    const headers = {
      'Accept': 'application/json',
    }

    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }

    const response = await fetch('/api/v2/chatbot/attachments', {
      method: 'POST',
      headers,
      body: formData,
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.message || `Upload error ${response.status}`)
    }

    const data = await response.json()
    return data.data || []
  },

  /**
   * Procesa respuesta del bot que puede venir como JSON string o objeto
   * @param {Object} result - Respuesta del servidor
   * @returns {Object} - Payload procesado
   */
  parseBotResponse(result) {
    let payload = result

    // Si viene todo como string JSON, intentar parsear
    if (typeof result.text === 'string' && result.text.trim().startsWith('{')) {
      try {
        payload = JSON.parse(result.text)
      } catch (e) {
        // Si falla el parse, usar el resultado original
        payload = result
      }
    }

    return payload
  },

  /**
   * Añade mensaje de typing al array de mensajes
   * @param {Array} messages - Array de mensajes
   * @returns {Array} - Array actualizado con mensaje typing
   */
  addTypingMessage(messages) {
    return [...messages, { role: 'typing', id: Date.now() }]
  },

  /**
   * Elimina mensajes de typing del array
   * @param {Array} messages - Array de mensajes
   * @returns {Array} - Array sin mensajes typing
   */
  removeTypingMessages(messages) {
    return messages.filter(m => m.role !== 'typing')
  },

  /**
   * Añade mensaje al array
   * @param {Array} messages - Array de mensajes
   * @param {Object} message - Mensaje a añadir { role, text, ... }
   * @returns {Array} - Array actualizado
   */
  addMessage(messages, message) {
    return [...messages, message]
  },

}
